import { useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { apiFetch } from "../lib/api";
import { uploadPhoto } from "../lib/upload";
import { EVENT_TYPES, EVENT_TYPE_ALBUMS } from "@family/shared";
import { GroupPicker } from "./GroupPicker";
import { useFeature } from "../hooks/useFeature";
import type {
  CreateEventRequest,
  Event,
  EventFaqItem,
  EventType,
  UpdateEventRequest,
} from "@family/shared";

interface EventFormProps {
  familySlug: string;
  event?: Event;
  onSaved?: (saved: Event) => void;
  onCancel?: () => void;
}

const EVENT_TYPE_LABELS: Record<EventType, string> = {
  wedding: "Wedding",
  funeral: "Funeral / Memorial",
  reunion: "Family Reunion",
  birthday: "Birthday",
  holiday: "Holiday / Celebration",
  custom: "Other",
};

export function EventForm({
  familySlug,
  event,
  onSaved,
  onCancel,
}: EventFormProps) {
  const isEdit = Boolean(event);

  const [title, setTitle] = useState(event?.title ?? "");
  const [description, setDescription] = useState(event?.description ?? "");
  const [eventType, setEventType] = useState<string>(event?.eventType ?? "custom");
  const [eventDate, setEventDate] = useState(event?.eventDate ?? "");
  const [endDate, setEndDate] = useState(event?.endDate ?? "");
  // An existing event without a date opens with "not set yet" ticked.
  const [dateTba, setDateTba] = useState(Boolean(event && !event.eventDate));
  // Optional date poll for an undated event: dates members can vote on.
  const [dateOptions, setDateOptions] = useState<string[]>(
    event?.dateOptions.map((o) => o.date) ?? [],
  );
  const [location, setLocation] = useState(event?.location ?? "");
  const [venue, setVenue] = useState(event?.venue ?? "");
  const [city, setCity] = useState(event?.city ?? "");
  const [country, setCountry] = useState(event?.country ?? "");
  const [contactEmail, setContactEmail] = useState(event?.contactEmail ?? "");
  const [paymentUrl, setPaymentUrl] = useState(event?.paymentUrl ?? "");
  const [rsvpEnabled, setRsvpEnabled] = useState(event?.rsvpEnabled ?? true);
  const [liveEnabled, setLiveEnabled] = useState(event?.liveEnabled ?? false);
  const [liveAudience, setLiveAudience] = useState<"everyone" | "groups">(
    event?.liveAudience ?? "everyone",
  );
  const [liveGroupIds, setLiveGroupIds] = useState<string[]>(event?.liveGroupIds ?? []);
  // The live call is a beta feature: only people enrolled in it can turn it
  // on, but an event that already has it keeps the setting visible.
  const liveFeature = useFeature("live_reunion") === true || Boolean(event?.liveEnabled);
  const [visibility, setVisibility] = useState<"everyone" | "groups">(
    (event?.eventType as any) === "groups" ? "groups" : "everyone",
  );
  const [groupIds, setGroupIds] = useState<string[]>([]);
  const [faq, setFaq] = useState<EventFaqItem[]>(event?.faq ?? []);
  const [coverMediaId, setCoverMediaId] = useState<string | null>(
    event?.coverMediaId ?? null,
  );
  const [coverPreview, setCoverPreview] = useState<string | null>(
    event?.coverUrl ?? null,
  );
  const [coverUploading, setCoverUploading] = useState(false);
  const [coverProgress, setCoverProgress] = useState(0);
  const [coverError, setCoverError] = useState<string | null>(null);
  const coverInputRef = useRef<HTMLInputElement | null>(null);

  const save = useMutation<Event, Error, CreateEventRequest | UpdateEventRequest>({
    mutationFn: (body) =>
      isEdit
        ? apiFetch<Event>(
            `/families/${familySlug}/events/${event!.id}`,
            { method: "PATCH", body: JSON.stringify(body) },
          )
        : apiFetch<Event>(`/families/${familySlug}/events`, {
            method: "POST",
            body: JSON.stringify(body),
          }),
    onSuccess: onSaved,
  });

  async function onPickCover(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const localUrl = URL.createObjectURL(file);
    setCoverPreview(localUrl);
    setCoverUploading(true);
    setCoverProgress(0);
    setCoverError(null);
    try {
      const mediaId = await uploadPhoto(familySlug, file, {
        onProgress: setCoverProgress,
      });
      setCoverMediaId(mediaId);
    } catch (err) {
      setCoverError(err instanceof Error ? err.message : String(err));
      setCoverPreview(event?.coverUrl ?? null);
    } finally {
      setCoverUploading(false);
    }
  }

  function removeCover() {
    setCoverMediaId(null);
    setCoverPreview(null);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim() || (!dateTba && !eventDate)) return;
    if (coverUploading) return;
    const payload: CreateEventRequest = {
      title: title.trim(),
      description: description.trim() || undefined,
      eventType: eventType || undefined,
      // null (not undefined) so an edit clears a date that was set before.
      eventDate: dateTba ? null : eventDate,
      endDate: dateTba ? null : endDate || undefined,
      dateOptions: dateTba ? [...new Set(dateOptions.filter(Boolean))] : undefined,
      location: location.trim() || undefined,
      venue: venue.trim() || undefined,
      city: city.trim() || undefined,
      country: country.trim() || undefined,
      contactEmail: contactEmail.trim() || undefined,
      paymentUrl: paymentUrl.trim() || undefined,
      faq: faq.filter((f) => f.question.trim() || f.answer.trim()),
      rsvpEnabled,
      liveEnabled,
      liveAudience,
      liveGroupIds: liveAudience === "groups" ? liveGroupIds : [],
      coverMediaId,
    };
    save.mutate(payload);
  }

  function updateFaq(idx: number, patch: Partial<EventFaqItem>) {
    setFaq((prev) => prev.map((f, i) => (i === idx ? { ...f, ...patch } : f)));
  }
  function removeFaq(idx: number) {
    setFaq((prev) => prev.filter((_, i) => i !== idx));
  }
  function addFaq() {
    setFaq((prev) => [...prev, { question: "", answer: "" }]);
  }

  return (
    <form onSubmit={handleSubmit} className="card stack">
      <h3 style={{ fontSize: "1rem", margin: 0 }}>
        {isEdit ? "Edit event" : "New event"}
      </h3>

      <div>
        <label>Cover image</label>
        <div
          style={{
            display: "flex",
            gap: "var(--s-3)",
            alignItems: "flex-start",
          }}
        >
          <div
            style={{
              width: 160,
              height: 100,
              flexShrink: 0,
              borderRadius: 4,
              overflow: "hidden",
              background: "var(--c-bg-alt)",
              border: "1px solid var(--c-border)",
              position: "relative",
            }}
          >
            {coverPreview ? (
              <img
                src={coverPreview}
                alt=""
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: "block",
                  opacity: coverUploading ? 0.5 : 1,
                }}
                draggable={false}
                onContextMenu={(ev) => ev.preventDefault()}
              />
            ) : (
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "var(--c-text-subtle)",
                  fontSize: "0.85rem",
                }}
              >
                No cover
              </div>
            )}
            {coverUploading && (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  fontWeight: 700,
                  textShadow: "0 1px 2px rgba(0,0,0,0.8)",
                }}
              >
                {coverProgress}%
              </div>
            )}
          </div>

          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              gap: "var(--s-2)",
            }}
          >
            <input
              ref={coverInputRef}
              type="file"
              accept="image/*"
              onChange={onPickCover}
              style={{ display: "none" }}
            />
            <button
              type="button"
              className="btn-secondary"
              onClick={() => coverInputRef.current?.click()}
              disabled={coverUploading || save.isPending}
            >
              {coverUploading
                ? "Uploading…"
                : coverPreview
                  ? "Replace image"
                  : "Upload image"}
            </button>
            {coverPreview && !coverUploading && (
              <button
                type="button"
                className="btn-tertiary"
                onClick={removeCover}
                disabled={save.isPending}
                style={{ alignSelf: "flex-start", color: "var(--c-danger)" }}
              >
                Remove image
              </button>
            )}
            {coverError && (
              <p className="text-error" style={{ fontSize: "0.85rem" }}>
                {coverError}
              </p>
            )}
          </div>
        </div>
      </div>

      <div>
        <label htmlFor="ev-title">Title *</label>
        <input
          id="ev-title"
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
          disabled={save.isPending}
        />
      </div>

      <div>
        <label htmlFor="ev-type">Event type</label>
        <select
          id="ev-type"
          value={eventType}
          onChange={(e) => setEventType(e.target.value)}
          disabled={save.isPending}
          style={{
            width: "100%",
            background: "var(--c-bg)",
            color: "var(--c-text)",
            border: "1px solid var(--c-border-strong)",
            borderRadius: 4,
            padding: "10px 12px",
            fontSize: "1rem",
            fontFamily: "inherit",
          }}
        >
          {EVENT_TYPES.map((t) => (
            <option key={t} value={t}>{EVENT_TYPE_LABELS[t]}</option>
          ))}
        </select>
        {eventType && eventType !== "custom" && (
          <p className="text-subtle" style={{ fontSize: "0.8rem", marginTop: 4 }}>
            Suggested albums: {EVENT_TYPE_ALBUMS[eventType as EventType]?.join(", ")}
            {" "}— you can customize these after creating the event.
          </p>
        )}
      </div>

      <div>
        <label htmlFor="ev-desc">Description</label>
        <textarea
          id="ev-desc"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          disabled={save.isPending}
          rows={4}
          style={{
            width: "100%",
            background: "var(--c-bg)",
            color: "var(--c-text)",
            border: "1px solid var(--c-border-strong)",
            borderRadius: 4,
            padding: "14px 16px",
            fontSize: "1rem",
            fontFamily: "inherit",
            resize: "vertical",
          }}
        />
      </div>

      <div>
        <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <input
            type="checkbox"
            checked={dateTba}
            onChange={(e) => setDateTba(e.target.checked)}
            disabled={save.isPending}
            style={{ width: "auto", minHeight: 0, padding: 0, margin: 0 }}
          />
          <span>Date not set yet</span>
        </label>
        {dateTba && (
          <p className="text-muted" style={{ fontSize: "0.85rem", margin: "4px 0 0" }}>
            The event shows as “Date TBA” under Upcoming. Members can still RSVP
            to show interest. Add the date here once it is decided.
          </p>
        )}
      </div>

      {dateTba && (
        <div className="stack" style={{ gap: "var(--s-2)" }}>
          <div>
            <label style={{ marginBottom: 0 }}>Let members vote on a date (optional)</label>
            <p className="text-muted" style={{ fontSize: "0.85rem", margin: "4px 0 0" }}>
              Suggest a few dates and members tick the ones they can make. You
              pick the winner on the event page. Removing a date drops its votes.
            </p>
          </div>
          {dateOptions.map((d, i) => (
            <div key={i} style={{ display: "flex", gap: "var(--s-2)", alignItems: "center" }}>
              <input
                type="date"
                aria-label={`Date option ${i + 1}`}
                value={d}
                onChange={(e) =>
                  setDateOptions((prev) => prev.map((x, j) => (j === i ? e.target.value : x)))
                }
                disabled={save.isPending}
              />
              <button
                type="button"
                className="btn-tertiary"
                onClick={() => setDateOptions((prev) => prev.filter((_, j) => j !== i))}
                disabled={save.isPending}
              >
                Remove
              </button>
            </div>
          ))}
          {dateOptions.length < 10 && (
            <div>
              <button
                type="button"
                className="btn-tertiary"
                onClick={() => setDateOptions((prev) => [...prev, ""])}
                disabled={save.isPending}
              >
                + Add a date option
              </button>
            </div>
          )}
        </div>
      )}

      {!dateTba && (
        <div className="grid-2col">
          <div>
            <label htmlFor="ev-date">Start date *</label>
            <input
              id="ev-date"
              type="date"
              value={eventDate}
              onChange={(e) => setEventDate(e.target.value)}
              required
              disabled={save.isPending}
            />
          </div>
          <div>
            <label htmlFor="ev-end">End date</label>
            <input
              id="ev-end"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              disabled={save.isPending}
            />
          </div>
        </div>
      )}

      <div className="grid-2col">
        <div>
          <label htmlFor="ev-city">City</label>
          <input
            id="ev-city"
            type="text"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            disabled={save.isPending}
          />
        </div>
        <div>
          <label htmlFor="ev-country">Country</label>
          <input
            id="ev-country"
            type="text"
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            disabled={save.isPending}
          />
        </div>
      </div>

      <div>
        <label htmlFor="ev-venue">Venue (optional)</label>
        <input
          id="ev-venue"
          type="text"
          value={venue}
          onChange={(e) => setVenue(e.target.value)}
          disabled={save.isPending}
        />
      </div>

      <div>
        <label htmlFor="ev-location">Extra location notes</label>
        <input
          id="ev-location"
          type="text"
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          disabled={save.isPending}
        />
      </div>

      <div className="grid-2col">
        <div>
          <label htmlFor="ev-email">Contact email</label>
          <input
            id="ev-email"
            type="email"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            disabled={save.isPending}
          />
        </div>
        <div>
          <label htmlFor="ev-pay">Payment URL</label>
          <input
            id="ev-pay"
            type="url"
            value={paymentUrl}
            onChange={(e) => setPaymentUrl(e.target.value)}
            disabled={save.isPending}
          />
        </div>
      </div>

      <div>
        <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <input
            type="checkbox"
            checked={rsvpEnabled}
            onChange={(e) => setRsvpEnabled(e.target.checked)}
            disabled={save.isPending}
            style={{ width: "auto", minHeight: 0, padding: 0, margin: 0 }}
          />
          <span>Allow RSVPs</span>
        </label>
      </div>

      {liveFeature && (
        <div className="stack">
          <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input
              type="checkbox"
              checked={liveEnabled}
              onChange={(e) => setLiveEnabled(e.target.checked)}
              disabled={save.isPending}
              style={{ width: "auto", minHeight: 0, padding: 0, margin: 0 }}
            />
            <span>Live video get-together (trial)</span>
          </label>
          {liveEnabled && (
            <GroupPicker
              familySlug={familySlug}
              label="Who can join the call"
              visibility={liveAudience}
              selectedGroupIds={liveGroupIds}
              onVisibilityChange={setLiveAudience}
              onGroupsChange={setLiveGroupIds}
              disabled={save.isPending}
            />
          )}
        </div>
      )}

      <GroupPicker
        familySlug={familySlug}
        visibility={visibility}
        selectedGroupIds={groupIds}
        onVisibilityChange={setVisibility}
        onGroupsChange={setGroupIds}
        disabled={save.isPending}
      />

      <div>
        <div className="row-between">
          <label>FAQ</label>
          <button
            type="button"
            className="btn-tertiary"
            onClick={addFaq}
            disabled={save.isPending}
          >
            + Add Q&amp;A
          </button>
        </div>
        {faq.map((item, idx) => (
          <div
            key={idx}
            className="card stack"
            style={{ marginTop: "var(--s-2)" }}
          >
            <input
              type="text"
              placeholder="Question"
              value={item.question}
              onChange={(e) => updateFaq(idx, { question: e.target.value })}
              disabled={save.isPending}
            />
            <textarea
              placeholder="Answer"
              rows={2}
              value={item.answer}
              onChange={(e) => updateFaq(idx, { answer: e.target.value })}
              disabled={save.isPending}
              style={{
                width: "100%",
                background: "var(--c-bg)",
                color: "var(--c-text)",
                border: "1px solid var(--c-border-strong)",
                borderRadius: 4,
                padding: "14px 16px",
                fontSize: "1rem",
                fontFamily: "inherit",
                resize: "vertical",
              }}
            />
            <button
              type="button"
              className="btn-tertiary"
              onClick={() => removeFaq(idx)}
              disabled={save.isPending}
              style={{ alignSelf: "flex-start", color: "var(--c-danger)" }}
            >
              Remove
            </button>
          </div>
        ))}
      </div>

      {save.error && <p className="text-error">{save.error.message}</p>}

      <div style={{ display: "flex", gap: "var(--s-3)" }}>
        <button
          type="submit"
          disabled={save.isPending || !title.trim() || (!dateTba && !eventDate)}
        >
          {save.isPending ? "Saving…" : isEdit ? "Save changes" : "Create event"}
        </button>
        {onCancel && (
          <button
            type="button"
            className="btn-secondary"
            onClick={onCancel}
            disabled={save.isPending}
          >
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}
